import React from "react";
import { Link } from "react-router-dom";
import PassageChip from "../Passages/PassageChip";
import { DifficultyBadge } from "../Difficulty/Difficulty";
import { QuestionStatusBadge } from "./questionStatus";
import { codeUnderName } from "./cells";
import { focusRing } from "../../pages/Exams/TestBuilder/ui";
import { useRowLink } from "../../utils/rowLink";

/**
 * A question list on phones, one card per question: the name (a link to `getHref(question)`; `onOpen`
 * runs before it opens), the code, status, difficulty and passage (`onPassage(passage)`), and
 * `footer(question)` (e.g. author and date). With `selectable`, a checkbox (`checkbox(question)` gives its
 * {label, disabled}); `action(question)` renders in the card's corner (e.g. an edit link).
 */
export default function QuestionCards({
  questions,
  getHref,
  onOpen,
  onPassage,
  footer,
  selectable = false,
  selectedIds,
  onToggleSelect,
  checkbox,
  action,
}) {
  const rowLink = useRowLink();
  return (
    <ul className="flex flex-col gap-2.5">
      {questions.map((question) => {
        const href = getHref(question);
        const box = selectable ? checkbox?.(question) || {} : null;
        return (
          <li
            key={question.uuid}
            {...rowLink(href, { onOpen: () => onOpen?.(question) })}
            className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
          >
            <div className="flex items-start gap-3">
              {/* The label around the checkbox is the 44px a finger needs; a tap beside it opens the question. */}
              {box && (
                <label className={`-m-3 flex p-3 ${box.disabled ? "cursor-not-allowed" : "cursor-pointer"}`}>
                  <input
                    type="checkbox"
                    aria-label={box.label}
                    title={box.disabled ? box.label : undefined}
                    disabled={box.disabled}
                    checked={!box.disabled && !!selectedIds?.has(question.uuid)}
                    onChange={() => onToggleSelect(question)}
                    className="mt-0.5 h-5 w-5 cursor-pointer rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:opacity-50"
                  />
                </label>
              )}
              <div className="min-w-0 flex-1">
                <Link
                  to={href}
                  onClick={() => onOpen?.(question)}
                  className={`rounded text-[15px] font-semibold leading-snug text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing}`}
                >
                  {question.name || question.code || "—"}
                </Link>
                {codeUnderName(question) && (
                  <div className="mt-0.5 truncate font-mono text-xs text-slate-500">{question.code}</div>
                )}
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <QuestionStatusBadge status={question.status} />
                  <DifficultyBadge level={question.difficulty} />
                  <PassageChip passage={question.passage} onClick={onPassage ? () => onPassage(question.passage) : undefined} />
                </div>
                {footer && <div className="mt-2 text-sm text-slate-600">{footer(question)}</div>}
              </div>
              {action?.(question)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
